import React, { useMemo, useState } from 'react';
import LowerFooter from '../Components/LowerFooter';
import PhoneCountryInput from '../Components/PhoneCountryInput';
import { CLINIC_LEGAL } from '../data/clinicLegal';
import { useLocale } from '../context/LocaleContext';
import { fetchApi } from '../utils/authClient';
import { buildInternationalPhone, validatePhoneLocal } from '../utils/phoneInput';
import '../Components/PhoneCountryInput.css';
import './LibroReclamaciones.css';

const EMPTY = {
  type: 'reclamo',
  fullName: '',
  documentType: 'DNI',
  documentNumber: '',
  address: '',
  email: '',
  phoneLocal: '',
  isMinor: false,
  guardianName: '',
  guardianAddress: '',
  guardianEmail: '',
  guardianPhoneLocal: '',
  serviceDescription: '',
  serviceAmount: '',
  detail: '',
  request: '',
  consumerAcceptedTerms: false,
  privacyAccepted: false,
};

function fieldClass(errors, name) {
  return `lr_field${errors[name] ? ' lr_field--error' : ''}`;
}

export default function LibroReclamaciones() {
  const { t } = useLocale();
  const [form, setForm] = useState(EMPTY);
  const [phoneCountry, setPhoneCountry] = useState('PE');
  const [guardianPhoneCountry, setGuardianPhoneCountry] = useState('PE');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [sheet, setSheet] = useState(null);
  const [consumerCopySent, setConsumerCopySent] = useState(true);

  const provider = useMemo(
    () => ({
      legalName: CLINIC_LEGAL.legalName,
      tradeName: CLINIC_LEGAL.tradeName,
      ruc: CLINIC_LEGAL.ruc || t('libro.rucPending'),
      address: CLINIC_LEGAL.establishmentAddress,
    }),
    [t]
  );

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const next = {};
    const required = [
      'fullName',
      'documentNumber',
      'address',
      'email',
      'serviceDescription',
      'detail',
      'request',
    ];
    required.forEach((key) => {
      if (!String(form[key] || '').trim()) next[key] = true;
    });
    if (!form.type) next.type = true;
    const phone = validatePhoneLocal(phoneCountry, form.phoneLocal);
    if (!phone.ok) next.phoneLocal = true;
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      next.email = true;
    }
    if (form.isMinor) {
      ['guardianName', 'guardianAddress', 'guardianEmail'].forEach((key) => {
        if (!String(form[key] || '').trim()) next[key] = true;
      });
      const gPhone = validatePhoneLocal(guardianPhoneCountry, form.guardianPhoneLocal);
      if (!gPhone.ok) next.guardianPhoneLocal = true;
    }
    if (!form.consumerAcceptedTerms) next.consumerAcceptedTerms = true;
    if (!form.privacyAccepted) next.privacyAccepted = true;
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const phone = buildInternationalPhone(phoneCountry, form.phoneLocal);
    const guardianPhone = form.isMinor
      ? buildInternationalPhone(guardianPhoneCountry, form.guardianPhoneLocal)
      : '';

    setSubmitting(true);
    try {
      const res = await fetchApi('/api/libro-reclamaciones', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: form.type,
          fullName: form.fullName.trim(),
          documentType: form.documentType,
          documentNumber: form.documentNumber.trim(),
          address: form.address.trim(),
          phone,
          email: form.email.trim(),
          isMinor: form.isMinor,
          guardian: form.isMinor
            ? {
                fullName: form.guardianName.trim(),
                address: form.guardianAddress.trim(),
                phone: guardianPhone,
                email: form.guardianEmail.trim(),
              }
            : undefined,
          serviceDescription: form.serviceDescription.trim(),
          serviceAmount: form.serviceAmount.trim(),
          detail: form.detail.trim(),
          request: form.request.trim(),
          consumerAcceptedTerms: form.consumerAcceptedTerms,
          privacyAccepted: form.privacyAccepted,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.status) {
        setSubmitError(data.message || t('libro.submitError'));
        return;
      }
      setSheet(data.sheet);
      setConsumerCopySent(data.consumerCopySent !== false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(t('libro.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  const printSheet = () => {
    window.print();
  };

  return (
    <div className="lr_page">
      <div className="lr_inner">
        <header className="lr_header">
          <p className="lr_kicker">{t('libro.kicker')}</p>
          <h1 className="lr_title">{t('libro.title')}</h1>
          <p className="lr_lead">{t('libro.lead')}</p>
        </header>

        {sheet ? (
          <section className="lr_sheet" aria-live="polite">
            <div className="lr_sheet_head">
              <div>
                <p className="lr_kicker">{t('libro.sheetLabel')}</p>
                <h2 className="lr_title">{t('libro.sheetTitle')}</h2>
              </div>
              <div className="lr_actions">
                <button type="button" className="lr_btn" onClick={printSheet}>
                  {t('libro.print')}
                </button>
              </div>
            </div>
            <p className={`lr_status ${consumerCopySent ? 'lr_status--ok' : 'lr_status--error'}`}>
              {consumerCopySent ? t('libro.copySent') : t('libro.copyFailed')}
            </p>
            <table>
              <tbody>
                {[
                  [t('libro.sheetNumber'), sheet.sheetNumber],
                  [t('libro.idCode'), sheet.identificationCode],
                  [t('libro.submittedAt'), sheet.submittedAtLabel],
                  [t('libro.type'), sheet.type === 'queja' ? t('libro.queja') : t('libro.reclamo')],
                  [t('libro.provider'), sheet.provider.legalName],
                  [t('libro.tradeName'), sheet.provider.tradeName],
                  ['RUC', sheet.provider.ruc || t('libro.rucPending')],
                  [t('libro.establishment'), sheet.provider.address],
                  [t('libro.consumer'), sheet.consumer.fullName],
                  [
                    t('libro.document'),
                    `${sheet.consumer.documentType} ${sheet.consumer.documentNumber}`,
                  ],
                  [t('libro.address'), sheet.consumer.address],
                  [t('libro.phone'), sheet.consumer.phone],
                  [t('libro.email'), sheet.consumer.email],
                  ...(sheet.consumer.isMinor
                    ? [
                        [t('libro.guardianName'), sheet.consumer.guardian?.fullName],
                        [t('libro.guardianAddress'), sheet.consumer.guardian?.address],
                        [t('libro.guardianPhone'), sheet.consumer.guardian?.phone],
                        [t('libro.guardianEmail'), sheet.consumer.guardian?.email],
                      ]
                    : []),
                  [t('libro.service'), sheet.service.description],
                  [t('libro.amount'), sheet.service.amount || t('libro.amountEmpty')],
                  [t('libro.detail'), sheet.detail],
                  [t('libro.request'), sheet.request],
                ].map(([label, value]) => (
                  <tr key={label}>
                    <th>{label}</th>
                    <td>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="lr_obs">
              <span className="lr_label">{t('libro.observations')}</span>
              <p className="lr_obs_box">{t('libro.observationsHint')}</p>
            </div>
            <p className="lr_copy">{t('libro.recipient')}</p>
            <p className="lr_copy">{t('libro.notComplaint')}</p>
          </section>
        ) : null}

        {!sheet ? (
          <>
            <section className="lr_legal_box">
              <h2 className="lr_h2">{t('libro.defsTitle')}</h2>
              <dl className="lr_defs">
                <div>
                  <dt>{t('libro.reclamo')}</dt>
                  <dd>{t('libro.reclamoDef')}</dd>
                </div>
                <div>
                  <dt>{t('libro.queja')}</dt>
                  <dd>{t('libro.quejaDef')}</dd>
                </div>
              </dl>
              <p className="lr_note">{t('libro.deadlineNote')}</p>
            </section>

            <section className="lr_provider">
              <h2 className="lr_h2">{t('libro.providerTitle')}</h2>
              <div className="lr_provider_grid">
                <div>
                  <span className="lr_label">{t('libro.provider')}</span>
                  <div className="lr_value">{provider.legalName}</div>
                </div>
                <div>
                  <span className="lr_label">{t('libro.tradeName')}</span>
                  <div className="lr_value">{provider.tradeName}</div>
                </div>
                <div>
                  <span className="lr_label">RUC</span>
                  <div className="lr_value">{provider.ruc}</div>
                </div>
                <div>
                  <span className="lr_label">{t('libro.establishment')}</span>
                  <div className="lr_value">{provider.address}</div>
                </div>
              </div>
            </section>

            <form className="lr_form" onSubmit={handleSubmit} noValidate>
              <h2 className="lr_h2">{t('libro.formTitle')}</h2>
              {submitError ? <p className="lr_status lr_status--error">{submitError}</p> : null}

              <div className={fieldClass(errors, 'type')}>
                <span>{t('libro.type')}</span>
                <div className="lr_type">
                  <label>
                    <input
                      type="radio"
                      name="type"
                      value="reclamo"
                      checked={form.type === 'reclamo'}
                      onChange={() => setField('type', 'reclamo')}
                    />
                    <span>
                      <strong>{t('libro.reclamo')}</strong>
                      <small>{t('libro.reclamoHint')}</small>
                    </span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="type"
                      value="queja"
                      checked={form.type === 'queja'}
                      onChange={() => setField('type', 'queja')}
                    />
                    <span>
                      <strong>{t('libro.queja')}</strong>
                      <small>{t('libro.quejaHint')}</small>
                    </span>
                  </label>
                </div>
              </div>

              <div className="lr_grid" style={{ marginTop: 16 }}>
                <label className={fieldClass(errors, 'fullName')}>
                  <span>{t('libro.consumer')}</span>
                  <input
                    value={form.fullName}
                    onChange={(e) => setField('fullName', e.target.value)}
                    autoComplete="name"
                  />
                </label>
                <label className={fieldClass(errors, 'documentNumber')}>
                  <span>{t('libro.document')}</span>
                  <span className="lr_field" style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 8 }}>
                    <select
                      value={form.documentType}
                      onChange={(e) => setField('documentType', e.target.value)}
                    >
                      <option value="DNI">DNI</option>
                      <option value="CE">CE</option>
                      <option value="Pasaporte">Pasaporte</option>
                    </select>
                    <input
                      value={form.documentNumber}
                      onChange={(e) => setField('documentNumber', e.target.value)}
                    />
                  </span>
                </label>
                <label className={`${fieldClass(errors, 'address')} lr_field--full`}>
                  <span>{t('libro.address')}</span>
                  <input
                    value={form.address}
                    onChange={(e) => setField('address', e.target.value)}
                    autoComplete="street-address"
                  />
                </label>
                <label className={fieldClass(errors, 'email')}>
                  <span>{t('libro.email')}</span>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setField('email', e.target.value)}
                    autoComplete="email"
                  />
                </label>
                <div className={fieldClass(errors, 'phoneLocal')}>
                  <span id="lr_phone_label">{t('libro.phone')}</span>
                  <PhoneCountryInput
                    countryCode={phoneCountry}
                    onCountryCodeChange={setPhoneCountry}
                    local={form.phoneLocal}
                    onLocalChange={(value) => setField('phoneLocal', value)}
                    labelledBy="lr_phone_label"
                    compact
                  />
                </div>
                <label className={`${fieldClass(errors, 'isMinor')} lr_field--full lr_check`}>
                  <input
                    type="checkbox"
                    checked={form.isMinor}
                    onChange={(e) => setField('isMinor', e.target.checked)}
                  />
                  <span>{t('libro.isMinor')}</span>
                </label>
              </div>

              {form.isMinor ? (
                <div className="lr_grid" style={{ marginTop: 8 }}>
                  <label className={fieldClass(errors, 'guardianName')}>
                    <span>{t('libro.guardianName')}</span>
                    <input
                      value={form.guardianName}
                      onChange={(e) => setField('guardianName', e.target.value)}
                    />
                  </label>
                  <label className={fieldClass(errors, 'guardianEmail')}>
                    <span>{t('libro.guardianEmail')}</span>
                    <input
                      type="email"
                      value={form.guardianEmail}
                      onChange={(e) => setField('guardianEmail', e.target.value)}
                    />
                  </label>
                  <label className={`${fieldClass(errors, 'guardianAddress')} lr_field--full`}>
                    <span>{t('libro.guardianAddress')}</span>
                    <input
                      value={form.guardianAddress}
                      onChange={(e) => setField('guardianAddress', e.target.value)}
                    />
                  </label>
                  <div className={fieldClass(errors, 'guardianPhoneLocal')}>
                    <span id="lr_gphone_label">{t('libro.guardianPhone')}</span>
                    <PhoneCountryInput
                      countryCode={guardianPhoneCountry}
                      onCountryCodeChange={setGuardianPhoneCountry}
                      local={form.guardianPhoneLocal}
                      onLocalChange={(value) => setField('guardianPhoneLocal', value)}
                      labelledBy="lr_gphone_label"
                      compact
                    />
                  </div>
                </div>
              ) : null}

              <div className="lr_grid" style={{ marginTop: 16 }}>
                <label className={`${fieldClass(errors, 'serviceDescription')} lr_field--full`}>
                  <span>{t('libro.service')}</span>
                  <input
                    value={form.serviceDescription}
                    onChange={(e) => setField('serviceDescription', e.target.value)}
                    placeholder={t('libro.servicePlaceholder')}
                  />
                </label>
                <label className={fieldClass(errors, 'serviceAmount')}>
                  <span>{t('libro.amount')}</span>
                  <input
                    value={form.serviceAmount}
                    onChange={(e) => setField('serviceAmount', e.target.value)}
                    placeholder="S/"
                  />
                </label>
                <label className={`${fieldClass(errors, 'detail')} lr_field--full`}>
                  <span>{t('libro.detail')}</span>
                  <textarea
                    value={form.detail}
                    onChange={(e) => setField('detail', e.target.value)}
                  />
                </label>
                <label className={`${fieldClass(errors, 'request')} lr_field--full`}>
                  <span>{t('libro.request')}</span>
                  <textarea
                    value={form.request}
                    onChange={(e) => setField('request', e.target.value)}
                  />
                </label>
                <label className={`${fieldClass(errors, 'consumerAcceptedTerms')} lr_field--full lr_check`}>
                  <input
                    type="checkbox"
                    checked={form.consumerAcceptedTerms}
                    onChange={(e) => setField('consumerAcceptedTerms', e.target.checked)}
                  />
                  <span>{t('libro.acceptTerms')}</span>
                </label>
                <label className={`${fieldClass(errors, 'privacyAccepted')} lr_field--full lr_check`}>
                  <input
                    type="checkbox"
                    checked={form.privacyAccepted}
                    onChange={(e) => setField('privacyAccepted', e.target.checked)}
                  />
                  <span>{t('libro.acceptPrivacy')}</span>
                </label>
              </div>

              <div className="lr_actions">
                <button type="submit" className="lr_btn" disabled={submitting}>
                  {submitting ? t('libro.submitting') : t('libro.submit')}
                </button>
              </div>
              {errors.consumerAcceptedTerms || errors.privacyAccepted ? (
                <p className="lr_error">{t('libro.acceptError')}</p>
              ) : null}
            </form>
          </>
        ) : null}
      </div>
      <LowerFooter />
    </div>
  );
}
